import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, CreditCard, Rocket, Sparkles, ChevronRight, Clock } from 'lucide-react';

const HowItWorks = () => {
  const { t } = useTranslation();

  const steps = [
    {
      id: 1,
      icon: <ShoppingCart className="w-8 h-8" />,
      emoji: '🛒',
      titleKey: 'products.howItWorks.step1.title',
      descriptionKey: 'products.howItWorks.step1.description',
      gradient: 'from-orange-500 to-amber-600',
      bgGradient: 'from-orange-50 to-amber-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 2,
      icon: <CreditCard className="w-8 h-8" />,
      emoji: '💳',
      titleKey: 'products.howItWorks.step2.title',
      descriptionKey: 'products.howItWorks.step2.description',
      gradient: 'from-red-500 to-pink-600',
      bgGradient: 'from-red-50 to-pink-50',
      borderColor: 'border-red-200'
    },
    {
      id: 3,
      icon: <Rocket className="w-8 h-8" />,
      emoji: '🎯',
      titleKey: 'products.howItWorks.step3.title',
      descriptionKey: 'products.howItWorks.step3.description',
      gradient: 'from-purple-500 to-indigo-600',
      bgGradient: 'from-purple-50 to-indigo-50',
      borderColor: 'border-purple-200'
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-gradient-to-r from-orange-100 to-red-100 text-orange-600 rounded-full px-6 py-3 text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4 mr-2" />
            {t('products.howItWorks.badge')}
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {t('products.howItWorks.title')}
          </h2>
          <div className="flex items-center justify-center gap-2 text-xl text-gray-600 mb-2">
            <Clock className="w-5 h-5 text-green-500" />
            <p>{t('products.howItWorks.subtitle')}</p>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`relative bg-gradient-to-br ${step.bgGradient} rounded-3xl shadow-xl border-2 ${step.borderColor} p-8 hover:scale-[1.02] transition-all duration-300 group`}
            >
              {/* Step Number */}
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                {step.id}
              </div>

              {/* Decorative Background */}
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br from-orange-200 to-red-200 rounded-full opacity-20 blur-2xl"></div>

              {/* Icon */}
              <div className={`w-16 h-16 bg-gradient-to-r ${step.gradient} rounded-2xl flex items-center justify-center text-white shadow-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                {step.icon}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span>{step.emoji}</span>
                {t(step.titleKey)}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t(step.descriptionKey)}
              </p>

              {/* Arrow for desktop */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                  <ChevronRight className="w-8 h-8 text-orange-400" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Tagline */}
        <div className="text-center">
          <p className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
            {t('products.howItWorks.tagline')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;